/**
 * Primary navigation rail.
 *
 * One component renders in three placements so the markup cannot drift
 * between them:
 *   desktop (1280+)  docked, drag-resizable, collapsible to an icon rail
 *   tablet (640–1279) off-canvas drawer behind a hamburger
 *   phone (<640)      the same drawer, plus a 5-slot bottom tab bar
 *
 * `variant` only changes chrome (a close button in the drawer, tooltips when
 * collapsed); the link list itself is identical everywhere.
 */
import { forwardRef } from 'react';
import { NavLink } from 'react-router-dom';

import { navGroupsFor } from '../lib/nav-items';
import type { Persona } from '../lib/personas';

export interface SidebarNavProps {
  persona: Persona;
  tenantSlug: string;
  variant: 'docked' | 'drawer';
  /** Icon-only rail. Labels stay in the DOM as accessible names. */
  isCollapsed?: boolean;
  /** Drawer closes on navigation; the docked rail passes nothing. */
  onNavigate?: () => void;
}

/**
 * The ref lands on the scrollable `<nav>` itself so the shell can reset its
 * scroll offset on route change (the docked rail is its own scroll container).
 */
export const SidebarNav = forwardRef<HTMLElement, SidebarNavProps>(function SidebarNav(
  { persona, tenantSlug, variant, isCollapsed = false, onNavigate },
  ref
): JSX.Element {
  const sections = navGroupsFor(persona);

  return (
    <nav
      ref={ref}
      className={`sidebar__nav${isCollapsed ? ' sidebar__nav--collapsed' : ''}`}
      aria-label="Primary"
    >
      {sections.map((section) => (
        <div className="sidebar__section" key={section.group}>
          {/* The heading is hidden when collapsed but stays in the a11y tree,
              so screen-reader users keep the grouping the rail loses. */}
          <h2 className="sidebar__section-title">{section.group}</h2>
          <ul className="sidebar__list">
            {section.items.map((item) => (
              <li key={item.id}>
                <NavLink
                  to={item.view === '' ? `/app/${tenantSlug}` : `/app/${tenantSlug}/${item.view}`}
                  end={item.view === ''}
                  className="sidebar__link"
                  onClick={onNavigate}
                  // Collapsed: icon-only — aria-label for SR, title tooltip for pointer.
                  aria-label={isCollapsed ? item.label : undefined}
                  title={isCollapsed ? item.label : undefined}
                >
                  <span className="sidebar__link-icon" aria-hidden="true">
                    {item.icon}
                  </span>
                  <span className="sidebar__link-label">{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {variant === 'drawer' ? null : <div className="sidebar__nav-end" aria-hidden="true" />}
    </nav>
  );
});
